// The one table every Rules list draws (code review G-10, D-284): Vendors, Companies (Domains and Aliases), Codes,
// Guidance and Oversight. It was six copies of the same markup, each with its
// own row component and its own sort and bulk wiring.
//
// Two columns, Rule and Status (owner, 2026-08-31 and 2026-09-14): the rule is
// a full sentence that wraps, the status a dot and a word (`StatusDot`). A list
// may add narrow columns between them — Routing's Controller, Manager, Director
// and Oversight (owner, 2026-10-09 — D-336) — each sortable on its own text. The
// row opens the editor (`rowActivationProps`, owner, 2026-09-11), an Inactive
// row is dimmed, both headers sort on the text their cells print (owner,
// 2026-09-01), and a checkbox per row with Select All in the header's corner
// drives the Mark Active / Mark Inactive strip (`BulkBar`, owner, 2026-09-15;
// D-173).
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import {
  Body1,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { BulkBar, BulkCheckbox, BulkSelectAllCheckbox } from './BulkActions'
import { SortableHeaderCell } from './SortableHeaderCell'
import { StatusDot } from './StatusDot'
import { rowActivationProps, useClickableRowClass, useRoomyTableClass } from './tableDensity'
import { useBulkCellClass, useBulkMark } from '../lib/bulkSelection'
import { sortRows, useTableSort } from '../lib/tableSort'

const useStyles = makeStyles({
  empty: { color: tokens.colorNeutralForeground3, padding: '8px 2px' },
  // A wide row scrolls in its own box, never the page body.
  scroll: { overflowX: 'auto', maxWidth: '100%', minWidth: 0 },
  // `tableLayout: fixed`, so one long sentence cannot squash the Status column.
  table: {
    tableLayout: 'fixed',
    width: '100%',
    '& .fui-TableHeaderCell__button': {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  head: {
    '& .fui-TableHeaderCell__button': {
      fontSize: '10.5px',
      fontWeight: tokens.fontWeightSemibold,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: tokens.colorNeutralForeground3,
    },
  },
  // The sentence WRAPS rather than ellipsizing. Fluent puts a flex layout inside
  // the cell, which clamps to one line on its own, so both halves are told.
  // `pre-line`: a sentence still wraps, and a line break in it starts a new
  // line — the Uploader rule's two sentences (D-394).
  ruleCell: {
    whiteSpace: 'pre-line',
    wordBreak: 'break-word',
    '& .fui-TableCellLayout': { whiteSpace: 'pre-line' },
    '& .fui-TableCellLayout__main': { whiteSpace: 'normal' },
  },
  inactiveRow: { opacity: 0.55 },
  // A narrow column's cell and header, centred: a checkmark under its step.
  centered: {
    textAlign: 'center',
    '& .fui-TableHeaderCell__button': { justifyContent: 'center', width: '100%' },
  },
})

/** A narrow column a list adds between Rule and Status. */
export interface RuleTableColumn<T> {
  key: string
  header: string
  /** Its width, padding excluded (`app/README.md`, Fluent gotchas). */
  width: number
  cell: (row: T) => ReactNode
  /** What the header sorts on. */
  sortKey: (row: T) => string
}

interface RuleTableProps<T> {
  /** The rows the status and search filters leave, in any order: the table sorts. */
  rows: readonly T[]
  /** What shows instead of the table when `rows` is empty. */
  emptyText: string
  /** The table's accessible name, e.g. "Vendor name rules". */
  ariaLabel: string
  /** What Select All selects, e.g. "vendor name rules". */
  selectAllLabel: string
  keyOf: (row: T) => string
  isActive: (row: T) => boolean
  /** The rule as the sentence the row reads. */
  sentence: (row: T) => string
  /** The Status cell. A dot and a word unless a list says more (Guidance). */
  status?: (row: T) => ReactNode
  /** What the Status header sorts on. The word the dot prints unless given. */
  statusSortKey?: (row: T) => string
  /** Narrow columns between Rule and Status, left to right (D-336). */
  columns?: readonly RuleTableColumn<T>[]
  /** The bulk strip's write, one row at a time. */
  write: (row: T, active: boolean) => Promise<unknown>
  /** Singular, for the bulk toast: "rule", "guidance row". */
  noun: string
  onEdit: (row: T) => void
  /** Every bulk write landed: the toast's sentence. */
  onMarked: (message: string) => void
  onError: (message: string) => void
}

export function RuleTable<T>({
  rows,
  emptyText,
  ariaLabel,
  selectAllLabel,
  keyOf,
  isActive,
  sentence,
  status,
  statusSortKey,
  columns = [],
  write,
  noun,
  onEdit,
  onMarked,
  onError,
}: RuleTableProps<T>) {
  const s = useStyles()
  const roomy = useRoomyTableClass()
  const clickable = useClickableRowClass()
  const bulkCell = useBulkCellClass()
  const sort = useTableSort<string>()
  const ordered = useMemo(
    () =>
      sortRows(rows, sort.state, {
        rule: sentence,
        ...Object.fromEntries(columns.map((column) => [column.key, column.sortKey])),
        status: statusSortKey ?? ((row: T) => (isActive(row) ? 'Active' : 'Inactive')),
      }),
    [rows, sort.state, sentence, columns, statusSortKey, isActive],
  )
  const bulk = useBulkMark({ rows: ordered, keyOf, isActive, write, noun, onDone: onMarked, onError })

  return (
    <>
      <BulkBar selection={bulk.selection} busy={bulk.busy} onMark={bulk.mark} />
      {rows.length === 0 ? (
        <Body1 className={s.empty}>{emptyText}</Body1>
      ) : (
        <div className={s.scroll}>
          <Table size="small" aria-label={ariaLabel} className={mergeClasses(s.table, roomy)}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell className={bulkCell}>
                  <BulkSelectAllCheckbox selection={bulk.selection} label={selectAllLabel} />
                </TableHeaderCell>
                <SortableHeaderCell
                  className={s.head}
                  sortDirection={sort.directionFor('rule')}
                  onSort={() => sort.toggle('rule')}
                >
                  Rule
                </SortableHeaderCell>
                {columns.map((column) => (
                  <SortableHeaderCell
                    key={column.key}
                    style={{ width: column.width }}
                    className={mergeClasses(s.head, s.centered)}
                    sortDirection={sort.directionFor(column.key)}
                    onSort={() => sort.toggle(column.key)}
                  >
                    {column.header}
                  </SortableHeaderCell>
                ))}
                <SortableHeaderCell
                  style={{ width: 110 }}
                  className={s.head}
                  sortDirection={sort.directionFor('status')}
                  onSort={() => sort.toggle('status')}
                >
                  Status
                </SortableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ordered.map((row) => {
                const text = sentence(row)
                return (
                  <TableRow
                    key={keyOf(row)}
                    className={mergeClasses(clickable, !isActive(row) ? s.inactiveRow : undefined)}
                    {...rowActivationProps(() => onEdit(row), `Edit: ${text}`)}
                  >
                    <TableCell className={bulkCell}>
                      <BulkCheckbox selection={bulk.selection} rowKey={keyOf(row)} label={text} />
                    </TableCell>
                    <TableCell className={s.ruleCell}>{text}</TableCell>
                    {columns.map((column) => (
                      <TableCell key={column.key} className={s.centered}>
                        {column.cell(row)}
                      </TableCell>
                    ))}
                    <TableCell>{status ? status(row) : <StatusDot active={isActive(row)} />}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  )
}
